import React, { useCallback, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import { JournalEntryDrawerFormikConfig } from "@components/accounting/journalEntryDrawer/JournalEntryDrawer";
import { AddDocument } from "@components/documents/FinancialDocumentList/AddDocument";

export const JournalEntryAddDocument: React.FC = () => {
    const formikContext = useFormikContext<JournalEntryDrawerFormikConfig>();
    const { values, setFieldValue } = formikContext;
    const attachedDocumentIds = useMemo(
        () => values.journalEntry.financialDocumentIds ?? [],
        [values.journalEntry.financialDocumentIds],
    );

    // Keep the latest attached ids in a ref so `attachDocument` can stay stable
    // and always append to the current list. Document rows in the picker are
    // memoized, so a stale closure here would overwrite previously attached ids
    // instead of adding to them when attaching multiple documents in a row.
    const attachedDocumentIdsRef = useRef(attachedDocumentIds);
    attachedDocumentIdsRef.current = attachedDocumentIds;

    const attachDocument = useCallback(
        (documentId: number) => {
            const currentDocumentIds = new Set(attachedDocumentIdsRef.current);
            currentDocumentIds.add(documentId);
            setFieldValue(
                "journalEntry.financialDocumentIds",
                Array.from(currentDocumentIds),
            );
        },
        [setFieldValue],
    );

    const attachDocuments = useCallback(
        (documentIds: number[]) => {
            if (documentIds.length === 0) {
                return;
            }
            const currentDocumentIds = new Set(attachedDocumentIdsRef.current);
            documentIds.forEach((id) => currentDocumentIds.add(id));
            setFieldValue(
                "journalEntry.financialDocumentIds",
                Array.from(currentDocumentIds),
            );
        },
        [setFieldValue],
    );

    return (
        <AddDocument
            attachDocument={attachDocument}
            attachDocuments={attachDocuments}
            attachedDocumentIds={attachedDocumentIds}
        />
    );
};
